/**
 * Utility functions for global image optimization.
 * Optimizes image URLs (especially Unsplash and web images) by injecting:
 * - WebP format conversion (fm=webp / auto=format)
 * - Quality compression (q=75)
 * - Dynamic width scaling (w=...)
 */

export interface OptimizeImageOptions {
  width?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'jpg' | 'auto';
  fit?: 'crop' | 'max' | 'scale';
}

export const DEFAULT_FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fm=webp&fit=crop&w=800&q=75';
export const DEFAULT_HERO_BANNER_IMAGE = DEFAULT_FALLBACK_IMAGE;
export const DEFAULT_AD_BANNER_IMAGE = DEFAULT_FALLBACK_IMAGE;

/**
 * Normalizes any image URL or upload filename into an absolute, safe URL.
 */
export function sanitizeImageUrl(
  url?: string,
  fallback: string = DEFAULT_FALLBACK_IMAGE
): string {
  if (!url || typeof url !== 'string' || !url.trim()) return fallback;

  let cleanUrl = url.trim();

  // Reject invalid raw local filesystem paths
  if (/^[a-zA-Z]:\\/i.test(cleanUrl) || /^file:\/\//i.test(cleanUrl)) {
    return fallback;
  }

  // Ensure relative server upload and static paths have leading slash & proper folder
  if (cleanUrl.startsWith('upload_')) {
    cleanUrl = '/uploads/' + cleanUrl;
  } else if (cleanUrl.startsWith('uploads/')) {
    cleanUrl = '/' + cleanUrl;
  } else if (cleanUrl.startsWith('images/')) {
    cleanUrl = '/' + cleanUrl;
  } else if (cleanUrl.startsWith('src/')) {
    cleanUrl = '/' + cleanUrl;
  } else if (/^[a-zA-Z0-9_-]+\.(jpg|jpeg|png|webp|svg|gif)$/i.test(cleanUrl)) {
    cleanUrl = '/uploads/' + cleanUrl;
  }

  return cleanUrl;
}

/**
 * Returns an optimized WebP image URL with proper sizing and compression flags.
 */
export function getOptimizedImageUrl(
  url?: string,
  options: OptimizeImageOptions = {}
): string {
  if (!url || typeof url !== 'string' || !url.trim()) return DEFAULT_FALLBACK_IMAGE;

  let cleanUrl = sanitizeImageUrl(url, DEFAULT_FALLBACK_IMAGE);

  const { width = 800, quality = 75, format = 'webp', fit = 'crop' } = options;

  try {
    // If it's an Unsplash URL, manipulate search parameters directly
    if (cleanUrl.includes('images.unsplash.com')) {
      const parsedUrl = new URL(cleanUrl);
      parsedUrl.searchParams.set('fm', format === 'auto' ? 'webp' : format);
      parsedUrl.searchParams.set('auto', 'format');
      parsedUrl.searchParams.set('q', quality.toString());
      parsedUrl.searchParams.set('w', width.toString());
      parsedUrl.searchParams.set('fit', fit);
      return parsedUrl.toString();
    }

    // For other CDN or relative URLs, return clean normalized path
    return cleanUrl;
  } catch {
    return cleanUrl || DEFAULT_FALLBACK_IMAGE;
  }
}

/**
 * Ensures exact image resolution matching the user's selected variant or color
 * across Cart, Checkout, Order Placement, Order Confirmation, Account History,
 * and Admin Dashboard Alert Center / Inventory views.
 */
export function getItemDisplayImage(
  product?: any,
  selectedColor?: string,
  variant?: any
): string {
  if (!product && !variant) return DEFAULT_FALLBACK_IMAGE;

  // 1. Safe parse colors if string or array
  let safeColors: any[] = [];
  if (Array.isArray(product?.colors)) {
    safeColors = product.colors;
  } else if (typeof product?.colors === 'string' && product.colors.trim()) {
    try {
      const parsed = JSON.parse(product.colors);
      if (Array.isArray(parsed)) safeColors = parsed;
    } catch {}
  }

  // 2. Search product.colors for the matching color name selected by user or variant
  const colorToMatch = selectedColor || variant?.color;
  if (colorToMatch && safeColors.length > 0) {
    const matchedColor = safeColors.find(
      (c: any) => c && typeof c.name === 'string' && c.name.toLowerCase() === String(colorToMatch).toLowerCase()
    );
    if (matchedColor?.images && Array.isArray(matchedColor.images) && matchedColor.images.length > 0 && typeof matchedColor.images[0] === 'string' && matchedColor.images[0].trim()) {
      return sanitizeImageUrl(matchedColor.images[0]);
    }
  }

  // 3. Check product.images array or string (admin uploaded product images)
  if (Array.isArray(product?.images) && product.images.length > 0 && typeof product.images[0] === 'string' && product.images[0].trim()) {
    return sanitizeImageUrl(product.images[0]);
  }
  if (typeof product?.images === 'string' && product.images.trim()) {
    try {
      const parsed = JSON.parse(product.images);
      if (Array.isArray(parsed) && parsed[0] && typeof parsed[0] === 'string' && parsed[0].trim()) {
        return sanitizeImageUrl(parsed[0]);
      }
    } catch {
      const firstSplit = product.images.split(',')[0]?.trim();
      if (firstSplit) return sanitizeImageUrl(firstSplit);
    }
  }

  // 4. Fallback to product.colors[0] (primary color image) only if color images exist
  if (safeColors.length > 0 && safeColors[0]?.images && Array.isArray(safeColors[0].images) && safeColors[0].images.length > 0 && typeof safeColors[0].images[0] === 'string' && safeColors[0].images[0].trim()) {
    return sanitizeImageUrl(safeColors[0].images[0]);
  }

  // 5. Check direct product image / imageUrl field
  if (typeof product?.image === 'string' && product.image.trim()) {
    return sanitizeImageUrl(product.image);
  }
  if (typeof product?.imageUrl === 'string' && product.imageUrl.trim()) {
    return sanitizeImageUrl(product.imageUrl);
  }

  // 6. Check variant images
  if (variant?.images && Array.isArray(variant.images) && variant.images.length > 0 && typeof variant.images[0] === 'string' && variant.images[0].trim()) {
    return sanitizeImageUrl(variant.images[0]);
  }
  if (typeof variant?.image === 'string' && variant.image.trim()) {
    return sanitizeImageUrl(variant.image);
  }

  // 7. Fallback to product.variants[0]
  let safeVariants: any[] = [];
  if (Array.isArray(product?.variants)) {
    safeVariants = product.variants;
  } else if (typeof product?.variants === 'string' && product.variants.trim()) {
    try {
      const parsed = JSON.parse(product.variants);
      if (Array.isArray(parsed)) safeVariants = parsed;
    } catch {}
  }
  if (safeVariants.length > 0) {
    const firstVarImg = safeVariants[0]?.images?.[0] || safeVariants[0]?.image;
    if (typeof firstVarImg === 'string' && firstVarImg.trim()) {
      return sanitizeImageUrl(firstVarImg);
    }
  }

  return DEFAULT_FALLBACK_IMAGE;
}

export const resolveProductImage = (product?: any, variant?: any, selectedColor?: string): string => {
  return getItemDisplayImage(product, selectedColor, variant);
};

/**
 * Generate responsive srcset string for Unsplash or supported image CDNs.
 */
export function generateSrcSet(url: string, widths: number[] = [320, 640, 800, 1024, 1200]): string | undefined {
  if (!url || !url.includes('images.unsplash.com')) return undefined;

  return widths
    .map(w => `${getOptimizedImageUrl(url, { width: w })} ${w}w`)
    .join(', ');
}
